/**
 * „Kein Ton nach Annahme" erkennen (T-28) — reine Funktionen, ohne SDK und
 * ohne DOM, damit sie sich ohne Browser prüfen lassen.
 *
 * Hintergrund: Die Anlage meldete einen Anruf als ANSWERED, der Nutzer hörte
 * aber weder Freizeichen noch Stimme. Ob Ton ankommt, lässt sich im Browser an
 * zwei Stellen ablesen: an den Audio-Tracks des Remote-Streams (kommen dort
 * überhaupt Pakete an?) und am versteckten <audio>-Element (spielt es?).
 */

/** Zustand des Remote-Audios in einem Augenblick — ohne Gerätenamen. */
export interface AudioZustand {
  /** Hängt überhaupt ein MediaStream am <audio>-Element? */
  hatStream: boolean;
  /** Audio-Tracks im Remote-Stream der aktiven Session. */
  tracksGesamt: number;
  /** Davon „live", aktiviert und nicht stumm (= es kommen Pakete an). */
  tracksLive: number;
  paused: boolean;
  /** HTMLMediaElement.readyState (0–4). */
  readyState: number;
  muted: boolean;
  /** Ausgabegerät (Edge/Chrome: sinkId, "" = Standard); null, wenn unbekannt. */
  sinkId: string | null;
  /** Letzter Fehler von play() mit anliegendem Stream (AbortError zählt nicht). */
  playFehler: string | null;
}

/** Nach so vielen ms ohne Ton gilt ein angenommener Anruf als „kein Ton". */
export const KEIN_TON_NACH_MS = 3000;

/** Warum kein Ton anliegt — oder null, wenn alles in Ordnung aussieht. */
export function keinTonGrund(z: AudioZustand | null): string | null {
  if (!z) return "kein Audio-Element";
  if (!z.hatStream) return "kein Remote-Stream am Audio-Element";
  if (z.tracksLive === 0) return z.tracksGesamt === 0 ? "keine Remote-Audio-Tracks" : "Remote-Audio-Tracks ohne Daten";
  if (z.playFehler) return `Wiedergabe blockiert (${z.playFehler})`;
  if (z.paused) return "Audio-Element pausiert";
  if (z.muted) return "Audio-Element stummgeschaltet";
  return null;
}

/**
 * true, wenn der Anruf seit mindestens KEIN_TON_NACH_MS angenommen ist und
 * trotzdem kein Ton anliegt. Gehaltene Anrufe zählen nicht (dort ist Stille
 * gewollt), ebenso nicht angenommene.
 */
export function keinTonNachAnnahme(
  z: AudioZustand | null,
  o: { answeredAt: number | null; jetzt: number; gehalten?: boolean },
): boolean {
  if (o.answeredAt === null || o.gehalten) return false;
  if (o.jetzt - o.answeredAt < KEIN_TON_NACH_MS) return false;
  return keinTonGrund(z) !== null;
}
